import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { UseFormSetError } from "react-hook-form/dist/types/form";

import { datadogLogs } from "@datadog/browser-logs";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import { selectCurrentUser } from "features/auth/auth-slice";
import { useGetPatientAlertsQuery } from "services/patient-alerts";
import {
  useDeactivatePatientAndCancelAppointmentsMutation,
  useGetPatientQuery,
  useUpdatePatientMutation
} from "services/patients";
import { useGetPatientStaffTasksQuery } from "services/staff-tasks";
import {
  PatientAlert,
  PatientAlertsQueryParams
} from "services/types/patient-alerts.type";
import { PatientInput } from "services/types/patients.type";

import { CLEARTONE_RESTRICTIONS } from "../../constants/feature-flags";
import { GROUP_PATIENT_CARE_COORDINATOR } from "../../constants/staff";
import usePagination from "../../hooks/use-pagination.hook";
import useUserRoles from "../../hooks/use-user-roles.hook";
import {
  mapPatientForProfile,
  setErrorsFromBackedToForm
} from "./use-patient.util";

const PatientContext = React.createContext<any>(null);

const emergencyContacts = ["", "secondary_", "tertiary_"];
const baseFields = [
  "emergency_contact_relationship",
  "emergency_contact_relationship_other",
  "emergency_contact_first_name",
  "emergency_contact_last_name",
  "emergency_contact_email",
  "emergency_contact_phone",
  "emergency_contact_street_address_1",
  "emergency_contact_street_address_2",
  "emergency_contact_city",
  "emergency_contact_state",
  "emergency_contact_zip_code",
  "emergency_contact_country"
];

const allFields = emergencyContacts
  .flatMap((prefix) => baseFields.map((field) => `${prefix}${field}`))
  .join(",");

export const PATIENT_HEADER_QUERY = `{id,external_id,podium_id,podium_sync,is_active,first_name,middle_name,last_name,preferred_name,quickbooks_customer_id,title,suffix,birthdate,sex,marital_status,health_card_number,primary_contact_notes,available_credit,secondary_contact_notes,ssn,employer_name,employment_status,phone,email,street_address_1,street_address_2,city,state,country,zip_code,secondary_phone,secondary_phone_type,secondary_email,secondary_street_address_1,secondary_street_address_2,secondary_city,secondary_state,secondary_country,secondary_zip_code,${allFields},responsible_party_relationship,responsible_party_first_name,responsible_party_last_name,responsible_party_street_address_1,responsible_party_street_address_2,responsible_party_city,responsible_party_state,responsible_party_country,responsible_party_zip_code,responsible_party_phone,responsible_party_email,bill_to_responsible_party,hipaa_sign_date,preferred_provider,preferred_clinic{id,timezone,display_name,name,noah_provider,state},age_years,photo,next_appointment_moment,next_appointment{id,type{id,name},needs_reschedule,title,start_moment,clinic{name,timezone},status,patient_feedback_rating,staff_member{first_name,last_name}},following_appointment_moment,following_appointment{id,type{id,name},needs_reschedule,title,start_moment,clinic{name,timezone},status,patient_feedback_rating,staff_member{first_name,last_name}},previous_appointment_moment,previous_appointment{id,type{id,name},needs_reschedule,title,start_moment,status,clinic{name,timezone},patient_feedback_rating,staff_member{first_name,last_name}},referral_source{id,name,is_active,referred_type},sub_referral_source{id,name,is_active,object_id,object_type,parent{id}},referring_physician,physicians,primary_care_physician,payment_source_type,balance,phone_type,preferred_communication_method,noah_id,noah_patient_id,patient_responsibility_balance,insurer_responsibility_balance,managed_care_responsibility_balance,secondary_insurance_policy{id,hearing_benefit,deductible,copay,notes,insurer{name}},primary_insurance_policy{id,hearing_benefit,deductible,copay,notes,insurer{name}},box_folder_id,crm_segment,managed_care_plan,hearing_loss_left,hearing_loss_right,left_ear_hearing_aid{id,status,is_in_use,display_name,delivered_at_display,service_plan_expiration_date,is_active,sale{id},inventory_product{id,status,condition,loanable,demoable,product{display_name},serial_number,manufacturer_warranty_expiration_date,loss_damage_warranty_expiration_date},description,extended_warranty_expiration_date,manufacturer_warranty_expiration_date,purchase_date,serial_number,ear,due_date,type,subtype},right_ear_hearing_aid{id,status,is_active,is_in_use,display_name,delivered_at_display,service_plan_expiration_date,sale{id},inventory_product{id,condition,loanable,demoable,product{display_name},serial_number,manufacturer_warranty_expiration_date,loss_damage_warranty_expiration_date},description,extended_warranty_expiration_date,manufacturer_warranty_expiration_date,purchase_date,serial_number,ear,due_date,type,subtype},hearing_loss_type_left,hearing_loss_type_right,hearing_loss_shape_left,hearing_loss_shape_right,most_recent_report{id,pure_tone_audiogram_report},assigned_tags,active_products_request{id},patient_notes,patient_staff_tasks,block_reviews,block_automation,created_at_display,do_not_mail,do_not_email,do_not_message,do_not_call,staff_dialog_assignee{id},noah_verified,migration_source_id,updated_at,last_audiogram_date,gender,gender_other,caption_call_lead,latest_right_noah_device,latest_left_noah_device,referred_count,created_by,suno_comms_id}`;

type PatientProviderProps = {
  patientId: string;
  children?: React.ReactNode;
};

export function PatientProvider({ patientId, children }: PatientProviderProps) {
  const flags = useFlags();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);

  const [currentPatientId, setCurrentPatientId] = useState<number>(-1);
  const [updatePatient, { isLoading: isUpdating }] = useUpdatePatientMutation();
  const [deactivatePatientAndCancelAppointment, { isLoading: isDeactivating }] =
    useDeactivatePatientAndCancelAppointmentsMutation();
  const { hasRole, isAdminOrBusinessOwner } = useUserRoles();

  const isCleartoneRestrictionsEnabled = flags[CLEARTONE_RESTRICTIONS];

  const editDisabled = useMemo(
    () =>
      Boolean(
        isCleartoneRestrictionsEnabled &&
          !(isAdminOrBusinessOwner || hasRole(GROUP_PATIENT_CARE_COORDINATOR))
      ),
    [isCleartoneRestrictionsEnabled, hasRole, isAdminOrBusinessOwner]
  );

  useEffect(() => {
    if (Boolean(patientId)) {
      setCurrentPatientId(Number.parseInt(patientId));
    } else {
      setCurrentPatientId(-1);
    }
  }, [patientId]);

  const {
    data: patient,
    refetch: rawRefetch,
    isLoading: isFetching,
    isUninitialized
  } = useGetPatientQuery(
    {
      patientId: currentPatientId,
      query: PATIENT_HEADER_QUERY
    },
    {
      skip: !Boolean(currentPatientId) || currentPatientId === -1
    }
  );

  const refetch = React.useCallback(() => {
    try {
      return rawRefetch().catch(() => {});
    } catch {
      // RTK error #38: query not started yet (skipped)
      return Promise.resolve();
    }
  }, [rawRefetch]);

  const [loadPatientTasks, setLoadPatientTasks] = useState(false);

  const { data: patientTasks } = useGetPatientStaffTasksQuery(
    { patient: currentPatientId, assignee: currentUser?.id },
    {
      skip:
        !Boolean(currentPatientId) ||
        !Boolean(currentUser?.id) ||
        !loadPatientTasks
    }
  );

  // Patient Alerts
  const [showDiscarded, setShowDiscarded] = useState(false);
  const [loadAlerts, setLoadAlerts] = useState(false);

  const alertsQueryParams = useMemo(
    () => ({
      patient: currentPatientId,
      discarded: showDiscarded
    }),
    [currentPatientId, showDiscarded]
  );

  const {
    data: alerts = [],
    fetchMore: fetchMoreAlerts,
    hasMoreData: hasMoreAlerts,
    isLoading: isLoadingAlerts,
    refreshData: refreshAlerts
  } = usePagination<PatientAlertsQueryParams, PatientAlert>({
    useQuery: useGetPatientAlertsQuery,
    limit: 50,
    params: alertsQueryParams,
    options: {
      skip:
        !loadAlerts ||
        Number(alertsQueryParams.patient) === -1 ||
        !alertsQueryParams.patient
    }
  });

  const updatePatientData = React.useCallback(
    (patientData: PatientInput) =>
      updatePatient({ id: currentPatientId, ...patientData }).then(
        (response) => {
          if ("error" in response) {
            dispatch(
              setCustomMessage(<ErrorSnackbarContent response={response} />)
            );
            dispatch(setOpen(true));
          } else {
            refetch();
            dispatch(setCustomMessage("Patient details updated"));
            dispatch(setOpen(true));
          }
        }
      ),
    [currentPatientId, refetch, dispatch, updatePatient]
  );

  const silentUpdatePatientData = React.useCallback(
    async (patientData: PatientInput, setError?: UseFormSetError<any>) => {
      datadogLogs.logger.log("Patient Profile update", {
        state: "pending",
        attributes: Object.keys(patientData),
        patient_id: currentPatientId
      });
      const response = await updatePatient({
        id: currentPatientId,
        ...patientData
      });
      if ("error" in response) {
        if (
          setError &&
          (response.error as any).status === 400 &&
          "data" in response.error
        ) {
          setErrorsFromBackedToForm({
            errors: response.error?.data as any,
            setError: setError
          });
        }

        dispatch(
          setCustomMessage(<ErrorSnackbarContent response={response} />)
        );
        dispatch(setOpen(true));
        datadogLogs.logger.error("Patient Profile update", {
          state: "failure",
          attributes: Object.keys(patientData),
          patient_id: currentPatientId
        });
      } else {
        datadogLogs.logger.log("Patient Profile update", {
          state: "success",
          attributes: Object.keys(patientData),
          patient_id: currentPatientId
        });
        refetch();
      }
      return response;
    },
    [currentPatientId, updatePatient, refetch, dispatch]
  );

  const deactivatePatientAndCancelAppointmentsData = React.useCallback(
    (patientData: PatientInput) =>
      deactivatePatientAndCancelAppointment({
        id: currentPatientId,
        ...patientData
      }).then((response) => {
        if ("error" in response) {
          dispatch(
            setCustomMessage(<ErrorSnackbarContent response={response} />)
          );
          dispatch(setOpen(true));
        } else {
          refetch();
          dispatch(
            setCustomMessage("Patient deactivated and appointments canceled")
          );
          dispatch(setOpen(true));
        }
      }),
    [currentPatientId, refetch, dispatch, deactivatePatientAndCancelAppointment]
  );

  const isLoading = isFetching || isUninitialized;

  const value = useMemo(() => {
    return {
      isLoading,
      isUninitialized,
      patientId,
      patient:
        currentPatientId !== -1 && patient
          ? mapPatientForProfile(patient)
          : undefined,
      refetch,
      updatePatientData,
      silentUpdatePatientData,
      deactivatePatientAndCancelAppointmentsData,
      isUpdating,
      isDeactivating,
      editDisabled,
      patientTasks,

      alerts,
      isLoadingAlerts,
      fetchMoreAlerts,
      refreshAlerts,
      hasMoreAlerts,
      showDiscarded,
      setShowDiscarded,

      // Lazy loading controls
      setLoadPatientTasks,
      setLoadAlerts
    };
  }, [
    alerts,
    currentPatientId,
    deactivatePatientAndCancelAppointmentsData,
    editDisabled,
    fetchMoreAlerts,
    hasMoreAlerts,
    isDeactivating,
    isLoading,
    isLoadingAlerts,
    isUninitialized,
    isUpdating,
    patient,
    patientId,
    patientTasks,
    refetch,
    refreshAlerts,
    showDiscarded,
    silentUpdatePatientData,
    updatePatientData
  ]);

  return (
    <PatientContext.Provider value={value}>{children}</PatientContext.Provider>
  );
}

export default function usePatient() {
  const context = React.useContext(PatientContext);
  if (!context) {
    throw new Error(`usePatient must be used within a PatientProvider`);
  }
  return context;
}
